import React, { useEffect, useMemo, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Button, Divider, Menu, MenuItem, MenuList } from "@mui/material";
import { WorkspaceDocumentIcon, WorkspaceFormIcon } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { TemplateContentPicker } from "components/Prompts/Deal-content-picker";
import { Notice } from "components/Prompts/Notice";
import {
  useActionHook,
  useDealParamsHooks,
  useDealRoleGuard,
  useIsViewerPermission,
  useModals,
  usePrompt,
  useUrlHooks
} from "hooks";
import { useDealActions, useUserRoleSelector, useUserSelector, useViewActions } from "store";
import { FolderKind, IFolder } from "store/types";
import { toastify } from "utils/tyostify";

import { RoleName } from "../../../constants";
import { ImportFolder } from "../../old-data-box-components/Import-folder/ImportFolder";
import { useFolderHook } from "../Hooks/use-folder-hook";

const panelMenuPaperSx = {
  borderRadius: "8px",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
  minWidth: 200,
  padding: "6px 0"
};

const panelMenuItemSx = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "15px",
  color: "#20293C",
  minHeight: 40,
  "&:hover": { backgroundColor: "#ECF1F8" }
};

interface Props {
  currentFolder: IFolder | null;
  refetchHandler: () => Promise<void>;
}

export const AddNewMenu: React.FC<Props> = ({ currentFolder, refetchHandler }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const onMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const onMenuClose = () => {
    setAnchorEl(null);
  };
  const { dealId } = useDealParamsHooks();
  const { queryParams } = useUrlHooks();
  const { section: sectionId } = queryParams;
  const directoryInputRef = useRef<HTMLInputElement | null>(null);

  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");

  const isAnalystViewerPermission = useIsViewerPermission("file_upload");
  const openPrompt = usePrompt();
  const { uploadDirectoryAction, getFoldersAction, getFilesAction, createTemplateFromDealAction } = useDealActions();
  const { setLeftSlider } = useViewActions();
  const { errorModal, filesUploadModal } = useModals();

  const { email } = useUserSelector();
  const userRole = useUserRoleSelector();
  const isAltiusAdmin = email?.endsWith("@altius.capital") && userRole?.name === RoleName.Admin;

  const { onCreateNewFolder, onAddForm, onAddDocument, onFileUpload } = useFolderHook({
    folder: {
      id: currentFolder?.id || -1,
      title: currentFolder?.title || "",
      is_private: currentFolder?.is_private ?? false,
      parent_id: currentFolder?.parent_id || null,
      deal_id: dealId,
      user_id: currentFolder?.user_id || 0,
      kind: currentFolder?.kind as FolderKind
    },
    refetchHandler
  });

  const { action: uploadDirectory } = useActionHook(uploadDirectoryAction, {
    onSuccess: () => {
      if (dealId) {
        getFoldersAction({ dealId });
        getFilesAction(dealId);
      }
    },
    onReject: () => {
      errorModal({ msg: "Something went wrong" });
    }
  });

  const onImportFolder: () => void = useMemo(
    () => () => setLeftSlider((closeSlider: () => void) => <ImportFolder closeSlider={closeSlider} />),
    [setLeftSlider]
  );

  const onImportFolderWithRole = useDealRoleGuard(onImportFolder, [RoleName.Admin, RoleName.Analyst]);

  useEffect(() => {
    if (!directoryInputRef.current) return;
    directoryInputRef.current.setAttribute("directory", "");
    directoryInputRef.current.setAttribute("webkitdirectory", "");
  }, [directoryInputRef]);

  const handleUploadFolder = () => {
    if (isAnalystViewerPermission) {
      openPrompt({ Component: Notice, props: { type: "trial" } });
      return;
    }
    if (!directoryInputRef.current) return;
    directoryInputRef.current.click();
  };

  const handleDirectoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !dealId) return;
    uploadDirectory({
      deal_id: dealId,
      files: e.target.files,
      ...(currentFolder?.kind === "folder" && currentFolder?.id !== -1 && { section_id: Number(sectionId) })
    });
    e.target.value = "";
  };

  const options = [
    {
      title: "New Folder",
      icon: <CreateNewFolderOutlinedIcon sx={{ fill: "#5d7782" }} />,
      onClick: () => {
        onMenuClose();
        onCreateNewFolder?.();
      },
      disabled: false
    },
    {
      title: "New Document",
      icon: <WorkspaceDocumentIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
      onClick: () => {
        onMenuClose();
        onAddDocument?.();
      },
      disabled: false
    },
    {
      title: "New Form",
      icon: <WorkspaceFormIcon sx={{ fill: "#5d7782", marginRight: "2px", padding: 0 }} />,
      onClick: () => {
        onMenuClose();
        onAddForm?.();
      },
      disabled: false
    },
    {
      title: "Upload File",
      icon: <FileUploadOutlinedIcon sx={{ fill: "#5d7782" }} />,
      onClick: () => {
        onMenuClose();
        onFileUpload?.();
      },
      disabled: false
    },
    {
      title: "Upload Folder",
      icon: <FileUploadOutlinedIcon sx={{ fill: "#5d7782" }} />,
      onClick: () => {
        onMenuClose();
        handleUploadFolder();
      },
      disabled: false
    },

    {
      title: "Import Template",
      icon: <DriveFolderUploadOutlinedIcon sx={{ fill: "#5d7782" }} />,
      onClick: () => {
        onMenuClose();
        onImportFolderWithRole?.();
      },
      disabled: !onImportFolderWithRole,
      separatorBefore: true
    },
    {
      title: "Create Template",
      icon: <FileCopyOutlinedIcon sx={{ fill: "#5d7782" }} />,
      onClick: async () => {
        onMenuClose();
        // The save runs inside onConfirm so the picker can keep the form open and
        // show inline validation (e.g. duplicate title) instead of a generic modal.
        const result = await openPrompt({
          Component: TemplateContentPicker,
          props: {
            onConfirm: async ({ templateName, sections }) => {
              await createTemplateFromDealAction({ dealId, title: templateName, sections });
            }
          }
        });
        if (!result) return;
        toastify({ message: `${result.templateName} saved successfully`, type: "success" });
      },
      disabled: false
    },
    {
      title: "Upload to Cloud",
      icon: <CloudUploadOutlinedIcon sx={{ fill: "#5d7782" }} />,
      onClick: () => {
        onMenuClose();
        filesUploadModal({
          isCloudMirror: true,
          dealId,
          onSuccessCallback: async () => {
            if (dealId) {
              await getFoldersAction({ dealId });
              getFilesAction(dealId);
            }
            await refetchHandler();
          }
        });
      },
      disabled: false,
      hidden: !isAltiusAdmin
    }
  ];

  return (
    <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isAnalystDealsViewerPermission}>
      <Button
        onClick={onMenuOpen}
        variant="contained"
        startIcon={<AddIcon />}
        sx={{
          bgcolor: "#10212E",
          color: "#fff",
          fontSize: "16px",
          fontWeight: 400
        }}
      >
        Add New
      </Button>
      <>
        <input
          ref={directoryInputRef}
          type="file"
          onChange={handleDirectoryChange}
          hidden
          style={{ width: 0, height: 0 }}
        />
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={onMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: panelMenuPaperSx } }}
          MenuListProps={{ sx: { padding: 0 } }}
        >
          <MenuList disablePadding>
            {options.map((option) => (
              <>
                {option.separatorBefore && <Divider sx={{ my: "6px", mx: "10px" }} />}
                <MenuItem onClick={option.onClick} disabled={option.disabled} sx={panelMenuItemSx}>
                  {option.icon}
                  {option.title}
                </MenuItem>
              </>
            ))}
          </MenuList>
        </Menu>
      </>
    </RoleComponentGuard>
  );
};
